import { z } from 'zod';
import { IsoDateTimeSchema, SegmentSchema, UuidSchema } from './common.js';

/**
 * Row-Shape einer Segment-Notiz, wie der Server sie ausliefert (#32).
 * `deleted_at`/`deleted_by` sind NICHT enthalten: gelöschte Notizen verlassen den
 * Server nie. `kfml_worklist_flagged_at`/`_by` bleiben serverintern (UI braucht sie nicht).
 */
export const SegmentNoteSchema = z.object({
  segment_note_id: UuidSchema,
  segment: SegmentSchema,
  body: z.string().min(1),
  mentions: z.array(UuidSchema).nullable().optional(),
  created_at: IsoDateTimeSchema,
  created_by: UuidSchema.nullable().optional(),
  done_at: IsoDateTimeSchema.nullable().optional(),
  done_by: UuidSchema.nullable().optional(),
  kfml_worklist: z.boolean(),
});
export type SegmentNote = z.infer<typeof SegmentNoteSchema>;

/**
 * Anlege-Input. `mentions` wird NICHT entgegengenommen (z.object strippt unbekannte
 * Keys) — der Server leitet sie aus `body` ab (Single Source: Anzeige == Erwähnung).
 * `kfml_worklist` optional (Default false): Checkbox „auf die Worklist" beim Anlegen.
 */
export const CreateSegmentNoteInputSchema = z.object({
  segment: SegmentSchema,
  body: z.string().min(1),
  kfml_worklist: z.boolean().optional(),
});
export type CreateSegmentNoteInput = z.infer<typeof CreateSegmentNoteInputSchema>;

/**
 * PATCH-Input: `done` (Fertig-Zustand) und/oder `kfml_worklist` (Worklist-Flag).
 * Beide optional, aber mindestens eines gesetzt — sonst ist der PATCH ein No-op.
 */
export const PatchSegmentNoteInputSchema = z
  .object({
    done: z.boolean().optional(),
    kfml_worklist: z.boolean().optional(),
  })
  .refine((v) => v.done !== undefined || v.kfml_worklist !== undefined, {
    message: 'at_least_one_field_required',
  });
export type PatchSegmentNoteInput = z.infer<typeof PatchSegmentNoteInputSchema>;

/**
 * Antwort des Bild-Uploads (#32): projektloses Bild, per URL im Notiz-`body`
 * referenziert. `url` ist API-relativ (ohne /api-Präfix), z. B.
 * `/v1/segment-notes/images/<image_id>`.
 */
export const SegmentNoteImageUploadSchema = z.object({
  image_id: UuidSchema,
  url: z.string(),
});
export type SegmentNoteImageUpload = z.infer<typeof SegmentNoteImageUploadSchema>;
